/**
 * <roi-pack-builder> — the Build-a-Pack PDP engine (v2, 2026-08-07).
 *
 * v2 is the conversion pass after CJ's "clunky" verdict. Flow model:
 * - The modal is TWO steps: a full-height design grid, then a focused
 *   detail step (big image + size row) for sized items. One-size hats
 *   add straight from the grid with inline ✓ feedback — no second step.
 * - The modal stays open while the pack fills ("2 of 3 picked" in the
 *   header) and closes itself when the pack is full. Editing a filled
 *   slot opens straight to the detail step and closes on update.
 * - The main CTA is never a dead end: incomplete pack → opens the
 *   picker at the first empty slot; full pack → submits.
 *
 * Cart model unchanged from v1: one POST /cart/add.js with N real
 * variant lines (properties[_pack_source]=bundle-pdp); the live
 * automatic tier discounts do the pricing. A synthetic
 * "shopify:cart:lines-update" event makes Horizon's cart-items refetch,
 * cart-icon update, and the auto-open drawer open.
 *
 * 2026-09-12 (ads memos 9/12 11:30 + 12:00): every step publishes a funnel
 * event through window.roiTrack (layout/theme.liquid → Shopify.analytics,
 * GA4, Meta): tier_selected / pack_tier_selected, pack_picker_opened,
 * pack_design_opened (+ GA4 view_item / Meta ViewContent), pack_size_selected,
 * pack_notify_me_clicked, pack_slot_filled, pack_partial_add_clicked,
 * pack_add_to_cart. In PDP embed mode the pack machinery (.roi-pe__pack) is
 * relocated to the buy box because the tier row now renders under the price.
 */
(function () {
  'use strict';
  if (window.customElements.get('roi-pack-builder')) return;

  // Ratified pack ladder. KEEP IN SYNC with the tier-card markup in
  // sections/roi-pack-pdp.liquid (prices are printed there too).
  var LADDER = {
    1: { total: 3800, each: null, save: 0 },
    2: { total: 6800, each: 3400, save: 800 },
    3: { total: 9600, each: 3200, save: 1800 },
    4: { total: 12800, each: 3200, save: 2400 },
    5: { total: 15000, each: 3000, save: 4000 },
  };

  var SIZE_SHORT = {
    Small: 'S',
    Medium: 'M',
    Large: 'L',
    'Extra large': 'XL',
    'Extra Large': 'XL',
    'XX-Large': '2XL',
    'XXX-Large': '3XL',
  };

  function money(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  // Always-two-decimals variant — the deal panel's save line reads
  // "Save $8.00" (CJ 2026-08-27), everything else stays whole-dollar.
  function moneyCents(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function sizeLabel(raw) {
    return SIZE_SHORT[raw] || raw;
  }

  function fmt(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] != null ? vars[k] : m;
    });
  }

  function pulse(el) {
    if (!el) return;
    el.classList.remove('is-pulse');
    void el.offsetWidth;
    el.classList.add('is-pulse');
  }

  // Funnel events — no-ops when the bridge isn't on the page.
  function track(name, data) {
    try {
      if (typeof window.roiTrack === 'function') window.roiTrack(name, data);
    } catch (e) {}
  }
  function trackStd(kind, data) {
    try {
      if (typeof window.roiTrackStd === 'function') window.roiTrackStd(kind, data);
    } catch (e) {}
  }

  var RoiPackBuilder = /** @type {any} */ (
    class extends HTMLElement {
      connectedCallback() {
        var dataEl = this.querySelector('[data-roi-pack-data]');
        try {
          this.items = JSON.parse(dataEl ? dataEl.textContent : '[]');
        } catch (e) {
          this.items = [];
        }
        this.copy = {};
        try {
          this.copy = JSON.parse(this.getAttribute('data-copy') || '{}');
        } catch (e) {}
        this.ring = parseInt(this.getAttribute('data-default-ring') || '2', 10) || 2;
        this.allowPartial = this.getAttribute('data-allow-partial') === 'true';
        // PDP embed mode (blocks/roi-pack-tiers.liquid): rings 1-3 live in a
        // normal product page's buy box. Ring 1 = the native PDP untouched;
        // ring 2/3 = pack mode (slot 0 seeded with THIS product's selected
        // variant, native buy buttons hidden via html.roi-pack-mode).
        this.embed = this.getAttribute('data-mode') === 'embed';
        this.pinnedHandle = this.getAttribute('data-pinned-handle') || null;
        this._pinnedSeeded = false;
        this.slots = []; // {key, handle, title, img, variantId, size, one, auto?}
        this.activeSlot = null;
        this.editIndex = null;
        this.modalStep = 'grid'; // 'grid' | 'detail'
        this.selectedKey = null;
        this.selectedVariant = null;
        this.busy = false;
        this.lastSize = null;

        this.tray = this.querySelector('[data-pp-tray]');
        this.counter = this.querySelector('[data-pp-counter]');
        this.priceEl = this.querySelector('[data-pp-price]');
        this.ctaBtn = this.querySelector('[data-pp-cta]');
        this.partialEl = this.querySelector('[data-pp-partial]');
        this.nudgeEl = this.querySelector('[data-pp-nudge]');
        this.modal = this.querySelector('dialog[data-pp-modal]');
        this.modalGrid = this.querySelector('[data-pp-designs]');
        this.stepGrid = this.querySelector('[data-pp-step-grid]');
        this.stepDetail = this.querySelector('[data-pp-step-detail]');
        this.modalSizes = this.querySelector('[data-pp-sizes]');
        this.modalSizeRow = this.querySelector('[data-pp-size-row]');
        this.modalConfirm = this.querySelector('[data-pp-confirm]');
        this.modalDone = this.querySelector('[data-pp-done]');
        this.modalBack = this.querySelector('[data-pp-back]');
        this.modalImg = this.querySelector('[data-pp-modal-img]');
        this.modalName = this.querySelector('[data-pp-modal-name]');
        this.modalCount = this.querySelector('[data-pp-modal-count]');
        // Detail-step gallery + details (build-a-pack page only; the PDP
        // tier block keeps the single hero image and these stay null).
        this.mGalTrack = this.querySelector('[data-pp-mgal-track]');
        this.mGalThumbs = this.querySelector('[data-pp-mgal-thumbs]');
        this.mDet = this.querySelector('[data-pp-mdet]');
        this.mDetBody = this.querySelector('[data-pp-mdet-body]');
        this.mDetLink = this.querySelector('[data-pp-mdet-link]');
        this.pcache = {};
        this.galKey = null;
        if (this.mGalTrack) this.mGalTrack.addEventListener('scroll', this.onGalScroll.bind(this), { passive: true });
        this.modalBody = this.querySelector('.roi-pp-modal__body');
        this.stickyBar = this.querySelector('[data-pp-sticky]');
        this.stickyLabel = this.querySelector('[data-pp-sticky-label]');

        // Highest tier button present (5 on the builder page, 3 in the
        // PDP embed) — nudges and setRing never exceed it.
        this.maxRing = 0;
        this.querySelectorAll('[data-pp-ring]').forEach(
          function (btn) {
            var n = parseInt(btn.getAttribute('data-pp-ring'), 10) || 0;
            if (n > this.maxRing) this.maxRing = n;
          }.bind(this)
        );
        if (!this.maxRing) this.maxRing = 5;

        this.errorEl = this.querySelector('[data-pp-error]');
        this.packEl = this.querySelector('.roi-pe__pack');
        // PDP embed (2026-09-12, ads memo 12:00 #1): the tier row now sits
        // directly under the price (template block order), so the pack
        // machinery — tray, deal panel, pack CTA, bigger-pack link — moves
        // down to where the native buy buttons live. The rings stay in the
        // block; the moved node gets its own copy of the click delegate.
        // Every element ref above was captured before the move.
        if (this.embed && this.packEl) {
          var buyBox = document.querySelector('.buy-buttons-block');
          if (buyBox && buyBox.parentNode) {
            buyBox.parentNode.insertBefore(this.packEl, buyBox);
            this.packEl.classList.add('roi-pe__pack--relocated');
            this.packEl.addEventListener('click', this.onClick.bind(this));
          }
        }
        this.addEventListener('click', this.onClick.bind(this));
        if (this.modal) {
          this.modal.addEventListener('click', this.onModalBackdrop.bind(this));
          this.modal.addEventListener('cancel', this.onModalCancel.bind(this));
          this.modal.addEventListener('close', this.unlockScroll.bind(this));
        }
        if (this.embed) {
          var self = this;
          // Keep the auto-seeded slot 0 following the native variant picker.
          // Horizon's picker dispatches a bubbling ProductSelectEvent whose
          // promise resolves with the newly selected variant.
          this._onProductSelect = function (e) {
            var p = /** @type {any} */ (e).promise;
            if (!p || typeof p.then !== 'function') return;
            p.then(function (r) {
              var res = r && r.detail ? r.detail.resource : null;
              if (res && res.id) self.syncPinnedVariant(String(res.id));
            }).catch(function () {});
          };
          this._selectHost = this.closest('.shopify-section') || document;
          this._selectHost.addEventListener('shopify:product:select', this._onProductSelect);
        }
        this.observeCta();
        this.renderDesignGrid();
        this.renderAll();
      }

      disconnectedCallback() {
        if (this._onProductSelect && this._selectHost) {
          this._selectHost.removeEventListener('shopify:product:select', this._onProductSelect);
        }
      }

      /* ---------- state helpers ---------- */

      filled() {
        return this.slots.length;
      }

      ringInfo(n) {
        return LADDER[n || this.ring];
      }

      itemByKey(key) {
        for (var i = 0; i < this.items.length; i++) {
          if (this.items[i].key === key) return this.items[i];
        }
        return null;
      }

      availableCount(item) {
        var n = 0;
        for (var i = 0; i < item.v.length; i++) if (item.v[i].a) n++;
        return n;
      }

      firstEmptyIndex() {
        return Math.min(this.slots.length, this.ring - 1);
      }

      /* ---------- scroll lock (iOS-proof: fixed body, restore position) ---------- */

      lockScroll() {
        if (this._locked) return;
        this._locked = true;
        this._lockY = window.scrollY || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = -this._lockY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }

      unlockScroll() {
        if (!this._locked) return;
        this._locked = false;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, this._lockY || 0);
      }

      /* ---------- top-level events ---------- */

      onClick(e) {
        var t = e.target;
        var ringBtn = t.closest('[data-pp-ring]');
        if (ringBtn) return this.setRing(parseInt(ringBtn.getAttribute('data-pp-ring'), 10));
        var slotEmpty = t.closest('[data-pp-slot-empty]');
        if (slotEmpty) return this.openModal(parseInt(slotEmpty.getAttribute('data-index'), 10), 'add');
        var slotRemove = t.closest('[data-pp-slot-remove]');
        if (slotRemove) {
          e.stopPropagation();
          return this.removeSlot(parseInt(slotRemove.closest('[data-pp-slot-filled]').getAttribute('data-index'), 10));
        }
        var slotFilled = t.closest('[data-pp-slot-filled]');
        if (slotFilled) return this.openModal(parseInt(slotFilled.getAttribute('data-index'), 10), 'edit');
        if (t.closest('[data-pp-cta]')) return this.onCta();
        if (t.closest('[data-pp-partial-link]')) {
          track('pack_partial_add_clicked', { filled: this.filled(), ring: this.ring, surface: this.surface() });
          return this.submit(this.slots.length);
        }
        if (t.closest('[data-pp-nudge-btn]')) {
          this.setRing(this.ring + 1);
          this.openModal(this.firstEmptyIndex(), 'add');
          return;
        }
        var mth = t.closest('[data-pp-mthumb]');
        if (mth) return this.galGoto(parseInt(mth.getAttribute('data-i'), 10));
        if (t.closest('[data-pp-back]')) return this.gotoGridStep();
        if (t.closest('[data-pp-done]') || t.closest('[data-pp-close]')) return this.closeModal();
        if (t.closest('[data-pp-confirm]')) return this.confirmSelection();
        // Sold-out designs stay tappable (2026-09-09): the detail step
        // shows every size greyed with the "tap it to get notified" hint.
        var peek = t.closest('[data-pp-peek]');
        if (peek) {
          e.stopPropagation();
          return this.peekDesign(peek.closest('[data-pp-design]').getAttribute('data-key'));
        }
        var design = t.closest('[data-pp-design]');
        if (design) {
          return this.onDesignTap(design.getAttribute('data-key'), design);
        }
        var sizeBtn = t.closest('[data-pp-size]');
        if (sizeBtn) {
          // Sold-out size: never selectable, but the tap becomes the
          // back-in-stock signup (demand signal per variant).
          if (sizeBtn.hasAttribute('data-soldout')) return this.notifyFor(sizeBtn.getAttribute('data-variant'));
          if (!sizeBtn.hasAttribute('disabled')) return this.selectSize(sizeBtn.getAttribute('data-variant'));
          return;
        }
        if (t.closest('[data-pp-notify]')) return this.notifyFor(t.closest('[data-pp-notify]').getAttribute('data-variant'));
        if (t.closest('[data-pp-sticky]')) return this.onCta();
      }

      /**
       * Opens the back-in-stock dialog (snippets/roi-bis-form.liquid via
       * assets/roi-back-in-stock.js) for a sold-out variant of the design
       * currently open in the modal. No-op when the engine isn't on the
       * page — the size simply stays greyed.
       */
      notifyFor(variantId) {
        var item = this.itemByKey(this.selectedKey);
        track('pack_size_selected', { variant_id: String(variantId), in_stock: false, handle: item ? item.h : null, surface: this.surface() });
        track('pack_notify_me_clicked', { variant_id: String(variantId), handle: item ? item.h : null, surface: this.surface() });
        if (!item || typeof window.roiBisOpen !== 'function') return;
        var variant = null;
        for (var i = 0; i < item.v.length; i++) {
          if (String(item.v[i].id) === String(variantId)) variant = item.v[i];
        }
        window.roiBisOpen({
          variantId: String(variantId),
          productTitle: item.t,
          variantTitle: item.one || !variant ? '' : sizeLabel(variant.s),
          productUrl: '/products/' + item.h,
          source: this.embed ? 'pack-embed' : 'pack-page',
        });
      }

      surface() {
        return this.embed ? 'pdp-embed' : 'pack-page';
      }

      // pack_design_opened + the standard view_item / ViewContent pair, so a
      // design opened inside the picker counts like a product page view.
      trackDesign(item) {
        var first = null;
        for (var i = 0; i < item.v.length; i++) {
          if (item.v[i].a) {
            first = item.v[i];
            break;
          }
        }
        track('pack_design_opened', { product_id: item.pid, handle: item.h, title: item.t, in_stock: !!first, surface: this.surface() });
        trackStd('view_item', {
          product_id: item.pid,
          handle: item.h,
          title: item.t,
          variant_id: first ? first.id : null,
          price_cents: LADDER[1].total,
          surface: this.surface(),
        });
      }

      setRing(n) {
        if (!LADDER[n] || n > this.maxRing) return;
        this.ring = n;
        track(this.embed ? 'tier_selected' : 'pack_tier_selected', { ring: n, handle: this.pinnedHandle, surface: this.surface() });
        if (this.embed) {
          if (n === 1) {
            // Back to single mode: the pack UI hides and the native PDP
            // returns — drop any half-built pack so hidden state can't go
            // stale behind the shopper's back.
            this.slots = [];
            this._pinnedSeeded = false;
          } else if (!this._pinnedSeeded) {
            this.seedPinnedSlot();
            this._pinnedSeeded = true;
          }
        }
        if (this.slots.length > n) this.slots = this.slots.slice(0, n);
        this.renderAll();
      }

      /* ---------- PDP embed: pinned slot 0 ---------- */

      nativeVariantId() {
        var el = document.querySelector('product-form-component input[name="id"]');
        return el && /** @type {HTMLInputElement} */ (el).value ? String(/** @type {HTMLInputElement} */ (el).value) : null;
      }

      /**
       * Builds a slot for the page's own product from the blob (colorway-
       * aware: picks the blob entry containing the given variant, so key,
       * title and image match the selected colorway).
       */
      pinnedSlotFor(variantId) {
        if (!this.pinnedHandle) return null;
        var item = null;
        var variant = null;
        var i, j, it;
        if (variantId) {
          for (i = 0; i < this.items.length; i++) {
            it = this.items[i];
            if (it.h !== this.pinnedHandle) continue;
            for (j = 0; j < it.v.length; j++) {
              if (String(it.v[j].id) === String(variantId) && it.v[j].a) {
                item = it;
                variant = it.v[j];
                break;
              }
            }
            if (item) break;
          }
        }
        if (!item) {
          for (i = 0; i < this.items.length && !item; i++) {
            it = this.items[i];
            if (it.h !== this.pinnedHandle) continue;
            for (j = 0; j < it.v.length; j++) {
              if (it.v[j].a) {
                item = it;
                variant = it.v[j];
                break;
              }
            }
          }
        }
        if (!item || !variant) return null;
        return {
          key: item.key,
          handle: item.h,
          title: item.t,
          img: item.img,
          variantId: String(variant.id),
          size: item.one ? 'OS' : sizeLabel(variant.s),
          one: !!item.one,
          auto: true,
        };
      }

      seedPinnedSlot() {
        if (this.slots.length) return;
        var slot = this.pinnedSlotFor(this.nativeVariantId());
        if (slot) this.slots.push(slot);
      }

      syncPinnedVariant(variantId) {
        if (!this.embed || this.ring < 2) return;
        if (!this.slots[0] || !this.slots[0].auto) return;
        var slot = this.pinnedSlotFor(variantId);
        if (!slot || slot.variantId === this.slots[0].variantId) return;
        this.slots[0] = slot;
        this.renderAll();
      }

      removeSlot(i) {
        this.slots.splice(i, 1);
        this.renderAll();
      }

      onCta() {
        if (this.busy) return;
        if (this.filled() < this.ring) {
          this.openModal(this.firstEmptyIndex(), 'add');
          return;
        }
        this.submit(this.ring);
      }

      /* ---------- modal flow ---------- */

      openModal(slotIndex, mode) {
        if (!this.modal) return;
        this.activeSlot = slotIndex;
        this.editIndex = mode === 'edit' ? slotIndex : null;
        this.selectedKey = null;
        this.selectedVariant = null;
        if (mode === 'edit' && this.slots[slotIndex]) {
          this.selectedKey = this.slots[slotIndex].key;
          this.selectedVariant = this.slots[slotIndex].variantId;
          this.modalStep = 'detail';
        } else {
          this.modalStep = 'grid';
        }
        this.renderModal();
        if (!this.modal.open) {
          this.lockScroll();
          this.modal.showModal();
          track('pack_picker_opened', { ring: this.ring, slot: slotIndex + 1, mode: mode, surface: this.surface() });
        }
        if (this.modalBody) this.modalBody.scrollTop = 0;
        if (this.mGalTrack) this.mGalTrack.scrollLeft = 0;
      }

      /* ---------- detail step: the product page in miniature (CJ 2026-09-11) ----------
         The blob carries one image per design. On the detail step we fetch
         /products/<handle>.js once per handle (cached for the session) and
         swap in every PDP image (model shots, flat lays, back prints) plus
         the description, so nobody has to leave the builder to see what
         they're buying. First paint is instant from the blob image; the
         fetch fills in behind it. Any failure leaves the single image. */
      fetchProduct(handle) {
        if (!this.pcache[handle]) {
          this.pcache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
            headers: { Accept: 'application/json' },
          })
            .then(function (r) {
              if (!r.ok) throw new Error(String(r.status));
              return r.json();
            })
            .catch(function () {
              return null;
            });
        }
        return this.pcache[handle];
      }

      imgPath(u) {
        return String(u || '').replace(/^https?:/, '').split('?')[0];
      }

      galleryImages(item, data) {
        var first = item.imgBig || item.img;
        var out = [{ src: first, alt: item.t }];
        var seen = {};
        seen[this.imgPath(first)] = true;
        if (!data || !data.media) return out;
        var media = data.media.filter(function (m) {
          return m.media_type === 'image' && m.preview_image && m.preview_image.src;
        });
        // Colourway entries (key "handle--colour"): prefer shots whose alt
        // text names the colour; fall back to everything if that's thin.
        var dash = item.key.indexOf('--');
        if (dash > -1) {
          var words = item.key.slice(dash + 2).split('-').filter(function (w) {
            return w.length > 2;
          });
          var mine = media.filter(function (m) {
            var a = String(m.alt || '').toLowerCase();
            return words.some(function (w) {
              return a.indexOf(w) > -1;
            });
          });
          if (mine.length >= 2) media = mine;
        }
        var self = this;
        media.forEach(function (m) {
          var p = self.imgPath(m.preview_image.src);
          if (seen[p]) return;
          seen[p] = true;
          var src = m.preview_image.src + (m.preview_image.src.indexOf('?') > -1 ? '&' : '?') + 'width=800';
          out.push({ src: src, alt: m.alt || item.t });
        });
        return out;
      }

      renderGallery(item) {
        var self = this;
        if (this.galKey === item.key) return;
        this.galKey = item.key;
        this.paintGallery(this.galleryImages(item, null));
        if (this.mDet) {
          this.mDet.hidden = true;
          this.mDet.open = false;
        }
        this.fetchProduct(item.h).then(function (data) {
          if (self.galKey !== item.key || !data) return;
          self.paintGallery(self.galleryImages(item, data));
          self.paintDetails(item, data);
        });
      }

      paintGallery(imgs) {
        if (!this.mGalTrack) return;
        var html = '';
        var thumbs = '';
        imgs.forEach(function (im, i) {
          var alt = String(im.alt || '').replace(/"/g, '&quot;');
          html +=
            '<div class="roi-pp-mgal__slide"><img src="' + im.src + '" alt="' + alt + '" width="280" height="350" loading="' + (i ? 'lazy' : 'eager') + '"></div>';
          thumbs +=
            '<button type="button" class="roi-pp-mgal__thumb' + (i ? '' : ' is-on') + '" data-pp-mthumb data-i="' + i + '" aria-label="Image ' + (i + 1) + ' of ' + imgs.length + '">' +
            '<img src="' + im.src.replace(/([?&])width=\d+/, '$1width=120') + '" alt="" loading="lazy"></button>';
        });
        this.mGalTrack.innerHTML = html;
        this.mGalTrack.scrollLeft = 0;
        if (this.mGalThumbs) {
          this.mGalThumbs.innerHTML = thumbs;
          this.mGalThumbs.hidden = imgs.length < 2;
        }
      }

      paintDetails(item, data) {
        if (!this.mDet || !this.mDetBody) return;
        var html = String(data.description || '')
          .replace(/<(script|style|iframe|object|embed|form)[\s\S]*?<\/\1>/gi, '')
          .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*')/gi, '');
        this.mDetBody.innerHTML = html;
        if (this.mDetLink) this.mDetLink.href = '/products/' + encodeURIComponent(item.h);
        this.mDet.hidden = false;
      }

      galGoto(i) {
        if (!this.mGalTrack) return;
        var kids = this.mGalTrack.children;
        if (!kids[i]) return;
        this.mGalTrack.scrollTo({ left: kids[i].offsetLeft - kids[0].offsetLeft, behavior: 'smooth' });
      }

      onGalScroll() {
        if (!this.mGalTrack || !this.mGalThumbs || this._galRaf) return;
        var self = this;
        this._galRaf = requestAnimationFrame(function () {
          self._galRaf = null;
          var kids = self.mGalTrack.children;
          if (!kids.length) return;
          var x = self.mGalTrack.scrollLeft + kids[0].offsetLeft;
          var best = 0;
          var bd = Infinity;
          for (var j = 0; j < kids.length; j++) {
            var d = Math.abs(kids[j].offsetLeft - x);
            if (d < bd) {
              bd = d;
              best = j;
            }
          }
          self.mGalThumbs.querySelectorAll('[data-pp-mthumb]').forEach(function (b, k) {
            b.classList.toggle('is-on', k === best);
          });
        });
      }

      closeModal() {
        if (!this.modal) return;
        if (this.modal.open) this.modal.close();
        this.unlockScroll();
      }

      onModalCancel() {
        this.unlockScroll();
      }

      onModalBackdrop(e) {
        if (e.target === this.modal) this.closeModal();
      }

      gotoGridStep() {
        this.modalStep = 'grid';
        this.selectedKey = null;
        this.selectedVariant = null;
        this.editIndex = null;
        this.renderModal();
        if (this.modalBody) this.modalBody.scrollTop = 0;
      }

      onDesignTap(key, cardEl) {
        var item = this.itemByKey(key);
        if (!item) return;
        this.trackDesign(item);
        if (item.one) {
          var v = item.v[0];
          if (!v) return;
          if (!v.a) {
            // Sold-out one-size item (hat): the tap is the signup.
            this.selectedKey = key;
            this.notifyFor(v.id);
            return;
          }
          this.commitSlot(item, v, cardEl);
          return;
        }
        this.selectedKey = key;
        this.selectedVariant = null;
        var want = this.editIndex != null && this.slots[this.editIndex] ? this.slots[this.editIndex].size : this.lastSize;
        if (want) {
          for (var i = 0; i < item.v.length; i++) {
            if (sizeLabel(item.v[i].s) === want && item.v[i].a) {
              this.selectedVariant = String(item.v[i].id);
              break;
            }
          }
        }
        this.modalStep = 'detail';
        this.renderModal();
        if (this.modalBody) this.modalBody.scrollTop = 0;
      }

      // "Details" chip on a one-size card (hats add straight from the grid,
      // so this is the only way to see their photos + description). Opens
      // the detail step with the single variant pre-selected: gallery,
      // details, then "Add to pack". Sized items just take the normal path.
      peekDesign(key) {
        var item = this.itemByKey(key);
        if (!item) return;
        if (!item.one) return this.onDesignTap(key, null);
        this.trackDesign(item);
        this.selectedKey = key;
        var v = item.v[0];
        this.selectedVariant = v && v.a ? String(v.id) : null;
        this.modalStep = 'detail';
        this.renderModal();
        if (this.modalBody) this.modalBody.scrollTop = 0;
      }

      selectSize(variantId) {
        this.selectedVariant = String(variantId);
        var sItem = this.itemByKey(this.selectedKey);
        track('pack_size_selected', { variant_id: String(variantId), in_stock: true, handle: sItem ? sItem.h : null, surface: this.surface() });
        this.renderModal();
        pulse(this.modalConfirm);
      }

      confirmSelection() {
        var item = this.itemByKey(this.selectedKey);
        if (!item || !this.selectedVariant) return;
        var variant = null;
        for (var i = 0; i < item.v.length; i++) {
          if (String(item.v[i].id) === this.selectedVariant) variant = item.v[i];
        }
        if (!variant || !variant.a) return;
        this.commitSlot(item, variant, null);
      }

      /**
       * Writes the selection into the pack, then routes: edit → close;
       * pack full → close + pulse the (now ready) main CTA; otherwise stay
       * open on the grid for the next pick.
       */
      commitSlot(item, variant, cardEl) {
        var slot = {
          key: item.key,
          handle: item.h,
          title: item.t,
          img: item.img,
          variantId: String(variant.id),
          size: item.one ? 'OS' : sizeLabel(variant.s),
          one: !!item.one,
        };
        var wasEdit = this.editIndex != null && this.slots[this.editIndex];
        if (wasEdit) {
          this.slots[this.editIndex] = slot;
        } else if (this.slots.length < this.ring) {
          this.slots.push(slot);
        }
        if (!slot.one) this.lastSize = slot.size;
        track('pack_slot_filled', {
          slot: wasEdit ? this.editIndex + 1 : this.slots.length,
          ring: this.ring,
          filled: this.filled(),
          variant_id: slot.variantId,
          handle: slot.handle,
          surface: this.surface(),
        });
        this.renderAll();

        if (cardEl) {
          cardEl.classList.remove('is-added');
          void cardEl.offsetWidth;
          cardEl.classList.add('is-added');
        }

        if (wasEdit) {
          this.editIndex = null;
          this.closeModal();
          return;
        }
        if (this.filled() >= this.ring) {
          var self = this;
          setTimeout(function () {
            self.closeModal();
            pulse(self.ctaBtn);
          }, cardEl ? 450 : 150);
          return;
        }
        this.selectedKey = null;
        this.selectedVariant = null;
        this.modalStep = 'grid';
        this.renderModal();
        if (this.modalCount) pulse(this.modalCount);
        if (!cardEl && this.modalBody) this.modalBody.scrollTop = 0;
      }

      /* ---------- cart ---------- */

      submit(count) {
        var self = this;
        var lines = this.slots.slice(0, count);
        if (!lines.length || this.busy) return;
        this.busy = true;
        this.renderCta();

        var agg = {};
        var order = [];
        lines.forEach(function (s) {
          if (!agg[s.variantId]) {
            agg[s.variantId] = 0;
            order.push(s.variantId);
          }
          agg[s.variantId] += 1;
        });
        var items = order.map(function (id) {
          return {
            id: parseInt(id, 10),
            quantity: agg[id],
            properties: { _pack_source: 'bundle-pdp' },
          };
        });

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items }),
        })
          .then(function (res) {
            if (!res.ok) {
              return res.json().then(function (data) {
                throw new Error((data && (data.description || data.message)) || 'Could not add the pack.');
              });
            }
            return res.json();
          })
          .then(function () {
            return fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .catch(function () { return null; });
          })
          .then(function (cart) {
            var ev = new Event('shopify:cart:lines-update', { bubbles: true });
            /** @type {any} */ (ev).action = 'add';
            /** @type {any} */ (ev).promise = Promise.resolve({
              cart: null,
              detail: { itemCount: cart ? cart.item_count : undefined, didError: false },
            });
            document.dispatchEvent(ev);
            track('pack_add_to_cart', {
              items: lines.length,
              ring: self.ring,
              total: LADDER[count] ? LADDER[count].total / 100 : undefined,
              variant_ids: order,
              cart_count: cart ? cart.item_count : undefined,
              surface: self.surface(),
            });
            self.slots = [];
            if (self.embed) {
              // Pack added: fall back to single mode so the PDP reads
              // normal again behind the opened drawer.
              self.ring = 1;
              self._pinnedSeeded = false;
            }
            self.busy = false;
            self.renderAll();
            setTimeout(function () {
              var drawer = document.getElementById('cart-drawer');
              var themeDrawer = drawer ? drawer.closest('theme-drawer') : null;
              var isOpen = themeDrawer && (themeDrawer.hasAttribute('open') || /** @type {any} */ (themeDrawer).isOpen);
              if (drawer && !isOpen) {
                if (typeof (/** @type {any} */ (drawer)).toggle === 'function') /** @type {any} */ (drawer).toggle();
                else if (typeof (/** @type {any} */ (drawer)).showDialog === 'function') /** @type {any} */ (drawer).showDialog();
              }
            }, 350);
          })
          .catch(function (err) {
            self.busy = false;
            self.renderCta();
            var note = self.errorEl || self.querySelector('[data-pp-error]');
            if (note) {
              note.textContent = err && err.message ? err.message : 'Something went wrong — try again.';
              note.hidden = false;
              setTimeout(function () { note.hidden = true; }, 6000);
            }
          });
      }

      /* ---------- page rendering ---------- */

      renderAll() {
        this.renderRings();
        this.renderTray();
        this.renderPrice();
        this.renderCta();
        this.renderNudge();
        this.renderSticky();
        this.updateGridBadges();
        if (this.embed) {
          var packing = this.ring > 1;
          this.classList.toggle('roi-pe--packing', packing);
          // Block CSS keys off this to swap the native buy buttons +
          // sticky ATC for the pack CTA while a pack is being built.
          document.documentElement.classList.toggle('roi-pack-mode', packing);
        }
      }

      renderRings() {
        var self = this;
        this.querySelectorAll('[data-pp-ring]').forEach(function (btn) {
          var n = parseInt(btn.getAttribute('data-pp-ring'), 10);
          btn.classList.toggle('is-active', n === self.ring);
          btn.setAttribute('aria-pressed', n === self.ring ? 'true' : 'false');
        });
      }

      renderTray() {
        if (!this.tray) return;
        var html = '';
        for (var i = 0; i < this.ring; i++) {
          var s = this.slots[i];
          if (s) {
            html +=
              '<button type="button" class="roi-pp-slot roi-pp-slot--filled" data-pp-slot-filled data-index="' + i + '" aria-label="Edit item ' + (i + 1) + ': ' + s.title.replace(/"/g, '&quot;') + '">' +
              '<img class="roi-pp-slot__img" src="' + s.img + '" alt="" loading="lazy">' +
              '<span class="roi-pp-slot__edit" aria-hidden="true">✎</span>' +
              '<span class="roi-pp-slot__remove" data-pp-slot-remove role="button" aria-label="Remove item ' + (i + 1) + '">✕</span>' +
              '<span class="roi-pp-slot__meta"><span class="roi-pp-slot__name">' + s.title + '</span>' +
              (s.one ? '' : '<span class="roi-pp-slot__size">' + s.size + '</span>') +
              '</span></button>';
          } else {
            html +=
              '<button type="button" class="roi-pp-slot roi-pp-slot--empty" data-pp-slot-empty data-index="' + i + '" aria-label="Add item to slot ' + (i + 1) + '">' +
              '<span class="roi-pp-slot__plus" aria-hidden="true">+</span></button>';
          }
        }
        this.tray.innerHTML = html;
        if (this.counter) {
          this.counter.textContent = fmt(this.copy.counter || '{filled} / {ring} picked', {
            filled: this.filled(),
            ring: this.ring,
          });
        }
      }

      renderPrice() {
        if (!this.priceEl) return;
        var info = this.ringInfo();
        var html = '';
        if (this.ring === 1) {
          // No savings at ring 1: total leads, upsell line beside it.
          html =
            '<span class="roi-pp-price__total">' + money(info.total) + '</span>' +
            '<span class="roi-pp-price__line">' + (this.copy.price_line_1 || 'One shirt or hat, full price.') + '</span>';
        } else {
          // Savings lead (CJ 2026-08-27): "Save $8.00" · "$34 each",
          // then a subtle "$68 total.", then the perk line.
          html =
            '<span class="roi-pp-price__save">' +
            fmt(this.copy.price_save_n || 'Save {save}', { save: moneyCents(info.save) }) +
            '</span>' +
            '<span class="roi-pp-price__line">' +
            fmt(this.copy.price_line_n || '{each} each', { each: money(info.each) }) +
            '</span>' +
            '<span class="roi-pp-price__totalsub">' +
            fmt(this.copy.price_total_n || '{total} total.', { total: money(info.total) }) +
            '</span>';
          var perks = '';
          if (this.ring >= 3) perks = this.copy.perks_3 || '+ Free shipping & free stickers';
          else perks = this.copy.perks_2 || '+ Free Mystery sticker pack';
          if (perks) html += '<span class="roi-pp-price__perks">' + perks + '</span>';
        }
        this.priceEl.innerHTML = html;
      }

      renderCta() {
        if (!this.ctaBtn) return;
        var filled = this.filled();
        var info = this.ringInfo();
        var label;
        if (this.busy) {
          label = this.copy.cta_busy || 'Adding…';
        } else if (filled === 0) {
          label = this.copy.cta_empty || 'Pick your designs';
        } else if (filled < this.ring) {
          var left = this.ring - filled;
          label = left === 1 ? this.copy.cta_one || 'One more to go' : fmt(this.copy.cta_more || 'Add {count} more', { count: left });
        } else {
          label =
            this.ring === 1
              ? fmt(this.copy.cta_ready_single || 'Add to cart — {total}', { total: money(info.total) })
              : fmt(this.copy.cta_ready || 'Add pack to cart — {total}', { total: money(info.total) });
        }
        // The CTA is never a dead end: incomplete → opens the picker.
        this.ctaBtn.disabled = this.busy;
        this.ctaBtn.classList.toggle('roi-pp__cta--picking', !this.busy && filled < this.ring);
        this.ctaBtn.textContent = label;

        if (this.partialEl) {
          var show = this.allowPartial && !this.busy && filled > 0 && filled < this.ring;
          this.partialEl.hidden = !show;
          if (show) {
            var pInfo = this.ringInfo(filled);
            this.partialEl.innerHTML =
              '<button type="button" class="roi-pp-partial__link" data-pp-partial-link>' +
              fmt(this.copy.partial_link || 'or add the {count} you’ve picked — {total}', {
                count: filled,
                total: money(pInfo.total),
              }) +
              '</button>';
          }
        }
      }

      renderNudge() {
        if (!this.nudgeEl) return;
        var show = this.filled() === this.ring && this.ring >= 1 && this.ring < this.maxRing && !this.busy;
        this.nudgeEl.hidden = !show;
        if (!show) return;
        var next = this.ringInfo(this.ring + 1);
        var text;
        if (this.ring === 1) text = this.copy.nudge_1 || 'Make it two: $34 each — save $8, stickers free';
        else if (this.ring === 2) text = this.copy.nudge_2 || 'One more makes it 3 for $96 — save $18, ships free';
        else text = fmt(this.copy.nudge_n || 'Add another at {each} each — {total} total', { total: money(next.total), each: money(next.each) });
        this.nudgeEl.innerHTML = '<button type="button" class="roi-pp-nudge__btn" data-pp-nudge-btn>' + text + '</button>';
      }

      renderSticky() {
        if (!this.stickyLabel) return;
        var filled = this.filled();
        var info = this.ringInfo();
        var state;
        if (filled === 0) state = this.copy.cta_empty || 'Pick your designs';
        else if (filled < this.ring) state = fmt(this.copy.counter || '{filled} / {ring} picked', { filled: filled, ring: this.ring });
        else state = (this.ring === 1 ? this.copy.cta_ready_single || 'Add to cart — {total}' : this.copy.cta_ready || 'Add pack to cart — {total}');
        this.stickyLabel.textContent = fmt(state, { total: money(info.total) });
      }

      observeCta() {
        var self = this;
        if (!this.stickyBar || !this.ctaBtn || typeof IntersectionObserver === 'undefined') return;
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              self.stickyBar.classList.toggle('is-shown', !entry.isIntersecting);
            });
          },
          { threshold: 0 }
        ).observe(this.ctaBtn);
      }

      /* ---------- modal rendering ---------- */

      renderDesignGrid() {
        if (!this.modalGrid) return;
        var self = this;
        var groups = { tee: [], hat: [] };
        this.items.forEach(function (item) {
          (groups[item.grp] || groups.tee).push(item);
        });
        var html = '';
        ['tee', 'hat'].forEach(function (grp) {
          var list = groups[grp];
          if (!list.length) return;
          var sorted = list.slice().sort(function (a, b) {
            return (self.availableCount(b) > 0 ? 1 : 0) - (self.availableCount(a) > 0 ? 1 : 0);
          });
          html +=
            '<h3 class="roi-pp-modal__group">' +
            (grp === 'tee' ? self.copy.group_tees || 'The shirts' : self.copy.group_hats || 'The hats') +
            '</h3><div class="roi-pp-modal__designs">';
          sorted.forEach(function (item) {
            var dead = self.availableCount(item) === 0;
            html +=
              '<button type="button" class="roi-pp-design' + (dead ? ' roi-pp-design--dead' : '') + '" data-pp-design data-key="' + item.key + '"' + (dead ? ' data-dead' : '') + '>' +
              '<span class="roi-pp-design__media"><img src="' + item.img + '" alt="" loading="lazy">' +
              (dead ? '<span class="roi-pp-design__soldout">' + (self.copy.soldout || 'Sold out') + '</span>' : '') +
              '<span class="roi-pp-design__added" aria-hidden="true">✓</span>' +
              (item.one && !dead ? '<span class="roi-pp-design__peek" data-pp-peek>Details</span>' : '') +
              '</span><span class="roi-pp-design__name">' + item.t + '</span>' +
              (item.r ? '<span class="roi-pp-design__stars">★ ' + item.r + '</span>' : '') +
              '</button>';
          });
          html += '</div>';
        });
        this.modalGrid.innerHTML = html;
      }

      updateGridBadges() {
        if (!this.modalGrid) return;
        var counts = {};
        this.slots.forEach(function (s) {
          counts[s.key] = (counts[s.key] || 0) + 1;
        });
        this.modalGrid.querySelectorAll('[data-pp-design]').forEach(function (btn) {
          var n = counts[btn.getAttribute('data-key')];
          if (n) btn.setAttribute('data-inpack', n);
          else btn.removeAttribute('data-inpack');
        });
      }

      renderModal() {
        var isGrid = this.modalStep === 'grid';
        var item = this.itemByKey(this.selectedKey);
        var self = this;

        if (this.stepGrid) this.stepGrid.hidden = !isGrid;
        if (this.stepDetail) this.stepDetail.hidden = isGrid;
        if (this.modalBack) this.modalBack.hidden = isGrid;
        if (this.modalDone) {
          this.modalDone.hidden = !isGrid;
          this.modalDone.textContent = this.copy.done_label || 'Done for now';
        }
        if (this.modalConfirm) this.modalConfirm.hidden = isGrid;

        if (this.modalName) {
          if (isGrid) {
            this.modalName.textContent = this.editIndex != null ? this.copy.modal_pick || 'Pick a design' : this.copy.modal_pick || 'Pick a design';
          } else {
            this.modalName.textContent = item ? item.t : '';
          }
        }
        if (this.modalCount) {
          this.modalCount.textContent = fmt(this.copy.counter || '{filled} / {ring} picked', {
            filled: this.filled(),
            ring: this.ring,
          });
        }

        if (!isGrid && item) {
          if (this.mGalTrack) {
            this.renderGallery(item);
          } else if (this.modalImg) {
            this.modalImg.src = item.imgBig || item.img;
            this.modalImg.hidden = false;
          }
          if (this.modalSizeRow) {
            this.modalSizeRow.hidden = !!item.one;
            if (!item.one && this.modalSizes) {
              var html = '';
              var anySoldOut = false;
              item.v.forEach(function (v) {
                var sel = String(v.id) === self.selectedVariant;
                if (!v.a) anySoldOut = true;
                // Sold-out sizes are greyed (never selectable) but stay
                // tappable: the tap opens the back-in-stock signup.
                html +=
                  '<button type="button" class="roi-pp-size' + (sel ? ' is-selected' : '') + (v.a ? '' : ' roi-pp-size--soldout') + '" data-pp-size data-variant="' + v.id + '"' + (v.a ? '' : ' data-soldout aria-disabled="true"') + ' aria-label="' + sizeLabel(v.s) + (v.a ? (v.q ? ', only ' + v.q + ' left' : '') : " — sold out, tap to get notified when it's back") + '">' +
                  sizeLabel(v.s) +
                  (v.a && v.q ? '<span class="roi-pp-size__qty">' + v.q + ' left</span>' : '') +
                  '</button>';
              });
              if (anySoldOut && typeof window.roiBisOpen === 'function') {
                html +=
                  '<p class="roi-pp-soldhint" data-pp-soldhint>' +
                  (self.copy.soldhint || "Missing your size? Tap it and we'll email you the day it's back.") +
                  '</p>';
              }
              this.modalSizes.innerHTML = html;
            }
          }
        }

        if (this.modalConfirm && !isGrid) {
          var ready = !!(item && this.selectedVariant);
          this.modalConfirm.disabled = !ready;
          this.modalConfirm.textContent = !ready
            ? item && item.one
              ? this.copy.soldout || 'Sold out'
              : this.copy.modal_size_btn || 'Select a size'
            : this.editIndex != null
              ? this.copy.modal_update_btn || 'Update slot'
              : this.copy.modal_add_btn || 'Add to pack';
        }

        this.updateGridBadges();
      }
    }
  );

  customElements.define('roi-pack-builder', RoiPackBuilder);
})();
